/** Hosts that serve the mid-market app. A host is added here and on the load balancer in the same release. */
export function buildHubHosts(nodeEnv = process.env.NODE_ENV) {
  return [
    "hub.netic.ai",
    // Staging hub on the staging edge (terraform/http-staging-edge); not routed to any other environment.
    "preview.hub.netic.ai",
    // Browsers resolve *.localhost to loopback, so `pnpm dev` serves the hub at http://hub.localhost:3000.
    ...(nodeEnv === "development" ? ["hub.localhost"] : []),
  ];
}

export const MIDMARKET_HUB_HOSTS = buildHubHosts();

/** Folder under app/ that holds the mid-market pages and routes. */
export const MIDMARKET_ROUTE_PREFIX = "/midmarket";

const MIDMARKET_SECURITY_HEADERS = [
  { key: "X-Content-Type-Options", value: "nosniff" },
  { key: "X-Frame-Options", value: "DENY" },
  { key: "Referrer-Policy", value: "strict-origin-when-cross-origin" },
  { key: "Strict-Transport-Security", value: "max-age=63072000; includeSubDomains" },
];

/** Build the regex Next matches a request host against. */
export function buildHostPattern(hosts = MIDMARKET_HUB_HOSTS) {
  return `(${hosts.map(host => host.replace(/\./g, "\\.")).join("|")})`;
}

/** Tell whether a request path is inside the mid-market route folder. */
export function isMidmarketPath(pathname) {
  return (
    !!pathname &&
    (pathname === MIDMARKET_ROUTE_PREFIX || pathname.startsWith(`${MIDMARKET_ROUTE_PREFIX}/`))
  );
}

/** Drop the /midmarket folder prefix so one path reads the same on the first load and after a client navigation. */
export function stripMidmarketPrefix(pathname) {
  return isMidmarketPath(pathname) ? pathname.slice(MIDMARKET_ROUTE_PREFIX.length) || "/" : pathname;
}

/**
 * Rewrites that put the hub hosts on app/midmarket and keep that folder dark on every other host.
 * Nothing is added for a host that is not listed, so enterprise hosts and paths are untouched.
 */
export function buildMidmarketRewrites(hosts = MIDMARKET_HUB_HOSTS) {
  if (hosts.length === 0) {
    return { beforeFiles: [] };
  }
  const host = buildHostPattern(hosts);
  return {
    beforeFiles: [
      {
        source: "/:path((?!_next/).*)",
        has: [{ type: "host", value: host }],
        destination: `${MIDMARKET_ROUTE_PREFIX}/:path`,
      },
      {
        source: `${MIDMARKET_ROUTE_PREFIX}/:path*`,
        missing: [{ type: "host", value: host }],
        destination: "/__midmarket-off",
      },
    ],
  };
}

/** Security headers for the hub hosts only. */
export function buildMidmarketHeaders(hosts = MIDMARKET_HUB_HOSTS) {
  if (hosts.length === 0) {
    return [];
  }
  return [
    {
      source: "/:path*",
      has: [{ type: "host", value: buildHostPattern(hosts) }],
      headers: MIDMARKET_SECURITY_HEADERS,
    },
  ];
}
